import React, { FC, PropsWithChildren } from 'react';

import {
  Avalanche,
  BSC,
  Chain,
  ChainId,
  Config,
  Mainnet,
  MetamaskConnector,
  Polygon,
  Sepolia,
  DAppProvider as UseDAppProvider,
} from '@usedapp/core';
import { EthereumProviderOptions } from '@walletconnect/ethereum-provider';
import type { QrModalOptions } from '@walletconnect/ethereum-provider/dist/types/types';

import { RabbyConnector } from '../connectors/RabbyConnector';
import { thirdwebBridgeConnector } from '../connectors/ThirdwebBridgeConnector';
import { WalletConnectConnector } from '../connectors/WalletConnectConnector';
import { WalletConnectUniversal } from '../connectors/WalletConnectUniversalConnector';
import {
  ACTIVE_ENV,
  WALLETCONNECT_PROJECT_ID,
  WALLET_CONNECT_APP_ICON_URL,
  WALLET_CONNECT_APP_METADATA,
} from '../constants';

import {
  RPC_CHAIN_ID,
  createReadOnlyProviderFactory,
  getPreferredRpcUrl,
} from '../rpc';

// name and native currency matched from here - https://github.com/smartbch/atomic-swap-frontend/blob/main/src/common/web3.ts
const SmartBCH: Chain = {
  chainId: 10000,
  chainName: 'smartBCH',
  nativeCurrency: {
    symbol: 'bch',
    name: 'BCH',
    decimals: 18,
  },
  isTestChain: false,
  isLocalChain: false,
  multicallAddress: '0xDdFB2A64E211BCdaeDaAB4f886A03EdE102CdD59',
  getExplorerAddressLink: (address: string) =>
    `https://www.smartscout.cash/address/${address}`,
  getExplorerTransactionLink: (transactionHash: string) =>
    `https://www.smartscout.cash/tx/${transactionHash}`,
  rpcUrl: getPreferredRpcUrl(RPC_CHAIN_ID.SMARTBCH),
  blockExplorerUrl: 'https://www.smartscout.cash/',
};

export const Hoodi: Chain = {
  chainId: 560048,
  chainName: 'Hoodi',
  isTestChain: true,
  isLocalChain: false,
  multicallAddress: '0xcA11bde05977b3631167028862bE2a173976CA11',
  multicall2Address: '0xcA11bde05977b3631167028862bE2a173976CA11',
  rpcUrl: getPreferredRpcUrl(RPC_CHAIN_ID.HOODI),
  blockExplorerUrl: 'https://hoodi.etherscan.io',
  nativeCurrency: {
    name: 'Hoodi Ether',
    symbol: 'ETH',
    decimals: 18,
  },
  getExplorerAddressLink: (address: string) =>
    `https://hoodi.etherscan.io/address/${address}`,
  getExplorerTransactionLink: (transactionHash: string) =>
    `https://hoodi.etherscan.io/tx/${transactionHash}`,
};

const isDev = ACTIVE_ENV === 'development' || ACTIVE_ENV === 'staging';

// Single URL per chain for WalletConnect (string-only). Reads/multicalls use
// readOnlyUrls below, which fail over to public RPCs when the paid node
// returns -32003 (daily request limit) or times out.
export const rpcMap: Record<number, string> = {
  [ChainId.Mainnet]: getPreferredRpcUrl(ChainId.Mainnet),
  [SmartBCH.chainId]: getPreferredRpcUrl(SmartBCH.chainId),
  [ChainId.Avalanche]: getPreferredRpcUrl(ChainId.Avalanche),
  [ChainId.BSC]: getPreferredRpcUrl(ChainId.BSC),
  [ChainId.Polygon]: getPreferredRpcUrl(ChainId.Polygon),
};

const readOnlyUrls: Record<
  number,
  ReturnType<typeof createReadOnlyProviderFactory>
> = {
  [ChainId.Mainnet]: createReadOnlyProviderFactory(ChainId.Mainnet),
  [SmartBCH.chainId]: createReadOnlyProviderFactory(SmartBCH.chainId),
  [ChainId.Avalanche]: createReadOnlyProviderFactory(ChainId.Avalanche),
  [ChainId.BSC]: createReadOnlyProviderFactory(ChainId.BSC),
  [ChainId.Polygon]: createReadOnlyProviderFactory(ChainId.Polygon),
};

if (isDev) {
  rpcMap[ChainId.Sepolia] = getPreferredRpcUrl(ChainId.Sepolia);
  rpcMap[Hoodi.chainId] = getPreferredRpcUrl(Hoodi.chainId);
  readOnlyUrls[ChainId.Sepolia] = createReadOnlyProviderFactory(
    ChainId.Sepolia,
  );
  readOnlyUrls[Hoodi.chainId] = createReadOnlyProviderFactory(Hoodi.chainId);
}

const baseWcConfig: EthereumProviderOptions = {
  projectId: WALLETCONNECT_PROJECT_ID,
  rpcMap,
  showQrModal: false,
  chains: [ChainId.Mainnet],
  optionalChains: [
    ChainId.Mainnet,
    ChainId.Avalanche,
    ChainId.Polygon,
    ChainId.BSC,
  ],
  methods: ['eth_sendTransaction', 'personal_sign'],
  events: ['chainChanged', 'accountsChanged'],
};

const baseQrModalOptions: QrModalOptions = {
  themeMode: 'dark',
  themeVariables: {
    '--w3m-font-family': 'Barlow, sans-serif',
    '--w3m-logo-image-url': WALLET_CONNECT_APP_ICON_URL,
    '--w3m-background-color': '#0F1823',
  },
};

const metadata = WALLET_CONNECT_APP_METADATA;

const config: Config = {
  readOnlyChainId: ChainId.Mainnet,
  readOnlyUrls,
  // TODO: Change to V2 when discontinuing smartBCH
  multicallVersion: 1 as const,
  networks: [Mainnet, Sepolia, Hoodi, Polygon, Avalanche, BSC, SmartBCH],
  fastMulticallEncoding: true,
  autoConnect: true,
  connectors: {
    metamask: new MetamaskConnector(),
    rabby: new RabbyConnector(),
    walletconnect: new WalletConnectConnector({
      ...baseWcConfig,
      qrModalOptions: {
        ...baseQrModalOptions,
        explorerRecommendedWalletIds: [
          '107bb20463699c4e614d3a2fb7b961e66f48774cb8f6d6c1aee789853280972c',
          '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0',
          '1ae92b26df02f0abca6304df07debccd18262fdf5fe82daa81593582dac9a369',
          'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96',
        ],
      },
      metadata,
    }),
    bcomwallet: new WalletConnectUniversal({
      ...baseWcConfig,
      metadata,
    }),
    wcuniversal: new WalletConnectUniversal({
      ...baseWcConfig,
      metadata,
    }),
    thirdwebBridge: thirdwebBridgeConnector,
  },
};

const DAppProvider: FC<PropsWithChildren> = ({ children }) => {
  return <UseDAppProvider config={config}>{children}</UseDAppProvider>;
};

export default DAppProvider;
